import React from 'react';
import memoize from 'memoize-one';
import { getDefault } from '~/utils/types';
import defaultComponents from '~/portals/components';
import getPortalDefinition from '~/portals/definition';
import { getFilesConfiguration } from '~/portals/files';
import { getRoutesConfiguration } from '~/portals/utils/config';
import AssetPortalActions from './Actions';
import AssetPortalSidebar from './Sidebar';
import AssetPortalVersionsTable from './VersionsTable';

const getPortalComponents = memoize(
  (type, portalConfiguration, assetTypesConfiguration) => {
    const localPortalConfiguration = getPortalDefinition(
      assetTypesConfiguration,
      type
    );
    const portalConfigurationComponents = {};

    if (portalConfiguration?.default?.actions) {
      portalConfigurationComponents.Actions = function Actions(props) {
        return (
          <AssetPortalActions
            portalConfiguration={portalConfiguration.default}
            {...props}
          />
        );
      };
    }

    if (portalConfiguration?.default?.sections) {
      portalConfigurationComponents.Sidebar = function Sidebar(props) {
        return (
          <AssetPortalSidebar
            portalConfiguration={portalConfiguration.default}
            {...props}
          />
        );
      };
    }

    if (portalConfiguration?.default?.metadata) {
      portalConfigurationComponents.VersionsTable = function VersionsTable(
        props
      ) {
        return (
          <AssetPortalVersionsTable
            portalConfiguration={portalConfiguration.default}
            {...props}
          />
        );
      };
    }

    return {
      ...defaultComponents,
      ...localPortalConfiguration,
      ...portalConfiguration?.default?.components,
      ...portalConfigurationComponents
    };
  }
);

export const processPlugins = memoize((portalConfiguration) => {
  if (!portalConfiguration?.default?.plugins) {
    return portalConfiguration;
  }

  const processProp = (prop) => {
    if (!portalConfiguration.default.plugins.some((plugin) => plugin[prop])) {
      return portalConfiguration.default[prop];
    }

    return (props) =>
      portalConfiguration.default.plugins.reduce(
        (accum, current) => {
          if (current[prop]) {
            const pluginItems = current[prop](props);

            return [...(accum ?? []), ...pluginItems];
          }

          return accum;
        },
        typeof portalConfiguration.default[prop] === 'function'
          ? portalConfiguration.default[prop](props)
          : portalConfiguration.default[prop]
      );
  };

  return {
    default: {
      ...portalConfiguration.default,
      files: processProp('files'),
      actions: processProp('actions'),
      metadata: processProp('metadata'),
      attributes: processProp('attributes'),
      sections: processProp('sections'),
      routes: processProp('routes')?.(),
      callouts: portalConfiguration.default.plugins.reduce((accum, current) => {
        if (current.callouts) {
          const pluginCallouts = current.callouts();

          return { ...pluginCallouts, ...accum };
        }

        return accum;
      }, portalConfiguration.default.callouts)
    }
  };
});

export const buildAssetContext = memoize(
  (
    asset,
    isDraft,
    isEdit,
    basePortalConfiguration,
    assetTypesConfiguration
  ) => {
    const assetTypeConfig = assetTypesConfiguration[asset.type];
    const portalConfiguration = processPlugins(basePortalConfiguration);

    return {
      asset,
      isDraft,
      isEdit,
      isTypeLoaded: !!portalConfiguration,
      components: getPortalComponents(
        asset.type,
        portalConfiguration,
        assetTypesConfiguration
      ),
      files: getFilesConfiguration(
        assetTypesConfiguration,
        asset.type,
        portalConfiguration
      ),
      routes: getRoutesConfiguration(portalConfiguration),
      typeAttributes: getDefault(assetTypesConfiguration, asset.type),
      versionsNavigation: portalConfiguration?.default?.versionsNavigation,
      search: portalConfiguration?.default?.search,
      isVersionsTableDisabled:
        portalConfiguration?.default?.isVersionsTableDisabled,
      attributes: portalConfiguration?.default?.attributes,
      actions: portalConfiguration?.default?.actions,
      callouts: portalConfiguration?.default?.callouts,
      metadata: portalConfiguration?.default?.metadata,
      metrics: portalConfiguration?.default?.metrics,
      sections: portalConfiguration?.default?.sections,
      visibility:
        assetTypeConfig?.publicPortalVisibility ??
        assetTypeConfig?.visibility ??
        'private'
    };
  }
);
